/**
 * Sampled curves as the editor shows them (W3-05; UX-043). Shared by the motion columns and the curve editor.
 *
 * Phira never plays a chart before second 0 (the chart time is clamped at 0 during the intro), so a curve sampled
 * at negative beats only shows the first segment extrapolated backwards: an eased first segment then sloped to
 * values the line never takes (a "Medium, slow end" 0 -> 275 over 2 beats reached about -1000 at beat -1.5 and the
 * value axis fitted to it). `holdBeforeChartStart` draws the value at beat 0 there instead.
 */

/** The shape of a sampled curve (render/timeline/curveCache.ts `SampledCurve`). */
export interface SampledValues {
  readonly beat0: number;
  readonly step: number;
  readonly values: Float32Array;
}

const held = new WeakMap<Float32Array, SampledValues>();

/**
 * The curve with every sample before beat 0 replaced by the sample at beat 0 (the curve itself when it starts at or
 * after 0). Results are memoised per sample buffer, so a cached curve is copied once.
 */
export function holdBeforeChartStart<T extends SampledValues>(curve: T): T {
  if (!(curve.beat0 < 0) || !(curve.step > 0) || curve.values.length === 0) return curve;
  const known = held.get(curve.values);
  if (known !== undefined && known.beat0 === curve.beat0 && known.step === curve.step) return known as T;
  const n = curve.values.length;
  const zero = Math.min(n - 1, Math.ceil(-curve.beat0 / curve.step));
  const v0 = curve.values[zero] ?? Number.NaN;
  const values = Float32Array.from(curve.values);
  for (let i = 0; i < zero; i++) values[i] = v0;
  const out = { ...curve, values };
  held.set(curve.values, out);
  return out;
}
